/**
 * Defines the rules for a custom animation to be applied to a view transition.
 */
export interface AnimationRule {
	/**
	 * The unique name of the view transition to target.
	 * Optional: If not provided and a `context.group` is active, it defaults to the group name.
	 * It's crucial this name matches the `data-vt` attribute of the participating element.
	 */
	viewTransitionName?: string;
	/** The type of animation to apply. Currently, only 'slide' is supported. */
	animationType: 'slide';
	/** The direction of the slide animation. */
	direction: 'left' | 'right' | 'up' | 'down';
	/**
	 * The duration of the animation (e.g., '0.3s', '300ms').
	 * Defaults to '0.3s' if not specified.
	 */
	duration?: string;
	/**
	 * The CSS timing function for the animation (e.g., 'ease-out', 'cubic-bezier(...)').
	 * Defaults to 'ease-out' if not specified.
	 */
	timingFunction?: string;
	/**
	 * For slide animations, determines the percentage of the slide for the old view
	 * on a forward transition, or the new view on a backward transition, creating an iOS-style effect.
	 * Expressed as a decimal (e.g., 0.3 for 30%). Defaults to 0.3.
	 */
	partialSlideFactor?: number;
}

export const DYNAMIC_STYLE_ID = 'smart-vt-dynamic-styles';

export function injectDynamicStyles(cssRules: string): void {
	let styleTag = document.getElementById(
		DYNAMIC_STYLE_ID,
	) as HTMLStyleElement | null;
	if (!styleTag) {
		styleTag = document.createElement('style');
		styleTag.id = DYNAMIC_STYLE_ID;
		document.head.appendChild(styleTag);
	}
	styleTag.textContent = cssRules;
}

export function clearDynamicStyles(): void {
	const styleTag = document.getElementById(DYNAMIC_STYLE_ID);
	if (styleTag) {
		styleTag.textContent = '';
	}
}

export function generateSlideAnimationCss(
	rule: AnimationRule & { viewTransitionName: string },
): string {
	const {
		viewTransitionName,
		direction,
		duration = '0.3s',
		timingFunction = 'ease-out', // User wants this as the primary default
		partialSlideFactor = 0.3,
	} = rule;

	const isForward = direction === 'left' || direction === 'up';

	const primaryTiming = timingFunction; // For the element sliding fully
	const secondaryTiming = 'ease-out'; // For the element sliding partially (can also be primaryTiming)

	let oldTransformFrom: string,
		oldTransformTo: string,
		oldOpacityFrom: number,
		oldOpacityTo: number;
	let newTransformFrom: string,
		newTransformTo: string,
		newOpacityFrom: number,
		newOpacityTo: number;

	const xPartial = `${partialSlideFactor * 100}%`;
	const yPartial = `${partialSlideFactor * 100}%`;
	const xFull = '100%';
	const yFull = '100%';
	const xNone = '0%';
	const yNone = '0%';

	if (isForward) {
		// Old view slides partially out, new view slides fully in
		oldOpacityFrom = 1;
		oldOpacityTo = 0.5; // Fades partially
		newOpacityFrom = 0;
		newOpacityTo = 1;

		switch (direction) {
			case 'left':
				oldTransformFrom = `translateX(${xNone})`;
				oldTransformTo = `translateX(-${xPartial})`;
				newTransformFrom = `translateX(${xFull})`;
				newTransformTo = `translateX(${xNone})`;
				break;
			case 'up':
				oldTransformFrom = `translateY(${yNone})`;
				oldTransformTo = `translateY(-${yPartial})`;
				newTransformFrom = `translateY(${yFull})`;
				newTransformTo = `translateY(${yNone})`;
				break;
			default: // Should not happen given type constraints
				oldTransformFrom =
					oldTransformTo =
					newTransformFrom =
					newTransformTo =
						'translate(0)';
				break;
		}
	} else {
		// Backward: Old view slides fully out, new view slides partially in
		oldOpacityFrom = 1;
		oldOpacityTo = 0; // Fades completely
		newOpacityFrom = 0;
		newOpacityTo = 1;

		switch (direction) {
			case 'right':
				oldTransformFrom = `translateX(${xNone})`;
				oldTransformTo = `translateX(${xFull})`;
				newTransformFrom = `translateX(-${xPartial})`;
				newTransformTo = `translateX(${xNone})`;
				break;
			case 'down':
				oldTransformFrom = `translateY(${yNone})`;
				oldTransformTo = `translateY(${yFull})`;
				newTransformFrom = `translateY(-${yPartial})`;
				newTransformTo = `translateY(${yNone})`;
				break;
			default: // Should not happen
				oldTransformFrom =
					oldTransformTo =
					newTransformFrom =
					newTransformTo =
						'translate(0)';
				break;
		}
	}

	const oldAnimationName = `smart-vt-old-${direction}-${viewTransitionName}`;
	const newAnimationName = `smart-vt-new-${direction}-${viewTransitionName}`;

	const oldAnimTiming = isForward ? secondaryTiming : primaryTiming;
	const newAnimTiming = isForward ? primaryTiming : secondaryTiming;

	// Ensure new view starts with 0 opacity if it's supposed to fade in
	const initialNewOpacityForStyle =
		newOpacityFrom === 0 && newOpacityTo === 1 ? 0 : '';

	return `
    @keyframes ${oldAnimationName} {
      from { transform: ${oldTransformFrom}; opacity: ${oldOpacityFrom}; }
      to { transform: ${oldTransformTo}; opacity: ${oldOpacityTo}; }
    }
    @keyframes ${newAnimationName} {
      from { transform: ${newTransformFrom}; opacity: ${newOpacityFrom}; }
      to { transform: ${newTransformTo}; opacity: ${newOpacityTo}; }
    }
    ::view-transition-old(${viewTransitionName}) {
      animation: ${duration} ${oldAnimTiming} forwards ${oldAnimationName};
      z-index: ${isForward ? 1 : 2};
    }
    ::view-transition-new(${viewTransitionName}) {
      animation: ${duration} ${newAnimTiming} forwards ${newAnimationName};
      ${initialNewOpacityForStyle !== '' ? `opacity: ${initialNewOpacityForStyle};` : ''}
      z-index: ${isForward ? 2 : 1};
    }
    ::view-transition-group(${viewTransitionName}) {
       perspective: 1000px;
       overflow: hidden; 
    }
  `;
}
